/**
 * SkillsListView — the nurse's fold, in the four states it can be in.
 *
 * This replaces ReviewInboxView, which split one attestation list into "for
 * review" and "published" by reading `state === 'accepted'` against
 * `state === 'published'`. That conflated two things the new model keeps apart:
 * a skill's STATE (proposed, pending, held, rejected) says whether the fold
 * supports it, and its VISIBILITY (private, published) says who may read it. A
 * held skill is private until she publishes it, and publishing is a decision
 * about consent, not a lifecycle step.
 *
 *  1. Proposed — the curator's candidates. Accept or reject, nothing else.
 *  2. Pending — what she is working toward: a target she has set on a skill she
 *     does not hold, or a certificate whose `period` has not started. NO
 *     accept and NO reject: nothing here is a lifecycle decision she takes.
 *     `proposal` is null in this state, so both verbs answer 409, and a
 *     certificate her institution issued has no business being filed under
 *     "the curator guessed this about you". A target is dropped through the
 *     target routes and a certificate starts on its own.
 *  3. Held — what the fold supports, each row showing its basis and carrying
 *     the publish or unpublish verb for that skill. She decides what becomes
 *     visible to colleagues; nothing is published on her behalf.
 *  4. Rejected — proposals she turned down, kept so the curator does not raise
 *     the same conclusion again. Read-only, and shown rather than hidden: a
 *     record the platform keeps and the nurse cannot see is a record she cannot
 *     account for.
 *
 * Clinical-safety copy: the visible unit is "familiar with / worked with",
 * never "expert", never a percentage or credential framing.
 *
 * DESIGN.md: flat hairline rows on surface-bg, pill actions, single accent,
 * scale-0.95 press, 44px targets. Tone is carried by label text, not colour.
 */

import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@frontend/components/ui/button'
import { Spinner } from '@frontend/components/ui/spinner'
import type { HeldSkill } from '@frontend/api/endpoints/skills'
import { useSkillTimeline } from '@frontend/hooks/useSkills'
import { foldPendingReason } from './pendingReason'
import { PeerEndorsements } from './PeerEndorsements'
import { ProposalCard } from './ProposalCard'
import { SkillBadges } from './SkillBadges'
import { heldSkillTitle } from '@/lib/skillTitle'

interface SkillsListViewProps {
  skills: HeldSkill[]
  onAccept: (skill: HeldSkill) => void
  onReject: (skill: HeldSkill) => void
  onPublish: (skill: HeldSkill) => void
  onUnpublish: (skill: HeldSkill) => void
  /** The key of the skill whose verb is in flight, so only that row is disabled. */
  busyKey?: string
}

function Section({
  id,
  title,
  emptyText,
  children,
  isEmpty,
}: {
  id: string
  title: string
  emptyText: string
  children: React.ReactNode
  isEmpty: boolean
}) {
  return (
    <section aria-labelledby={id}>
      <h2 id={id} className="mb-3 text-[15px] font-semibold text-[var(--color-surface-mute)]">
        {title}
      </h2>
      {isEmpty ? (
        <p className="rounded-[18px] border border-dashed border-[var(--color-surface-border)] px-5 py-6 text-[15px] text-[var(--color-surface-mute)]">
          {emptyText}
        </p>
      ) : (
        children
      )}
    </section>
  )
}

/**
 * One pending skill: what she is working toward, and WHY it is on her list.
 *
 * The reason is the whole content of the row. Without it "ECMO" sits under a
 * heading with no date, no basis and no action, and she cannot tell a goal she
 * set in June from a certificate that starts in November.
 *
 * The target comes free on the fold. The certificate's start day does not: a
 * `HeldSkill.period` is the fold's own interval, whose start is the earliest
 * support among ALL the records behind the skill, so it cannot name the day one
 * particular certificate takes effect — and in `pending` it is null anyway,
 * because nothing supports the skill yet. So the row reads the records behind
 * the skill on demand — the same read the proposal card makes for `reasoning`,
 * through the same hook and the same cache key, so a skill that is both is
 * fetched once.
 *
 * In `pending` no qualification has started yet (one that had would put the
 * skill in `held`), so every qualification the timeline returns is a future one
 * and its `period.start` is the day to show.
 *
 * A failed read says so. Rendering the row without its reason would claim she
 * is working toward this for no stated cause.
 */
function PendingReason({ skill }: { skill: HeldSkill }) {
  const { t } = useTranslation()
  const { data, isLoading, isError } = useSkillTimeline(skill.key, true)

  const { target, qualifications } = foldPendingReason(skill.target, data?.items ?? [])

  return (
    <div className="space-y-1">
      {target ? (
        <p className="text-[13px] text-[var(--color-surface-mute)]">
          {t('skills.pending.target', {
            due: target.due_date,
            basis: t(`skills.targetBasis.${target.basis}`),
          })}
        </p>
      ) : null}

      {isLoading ? (
        <p className="text-[13px] text-[var(--color-surface-mute)]">
          {t('skills.pending.recordsLoading')}
        </p>
      ) : isError ? (
        <p className="text-[13px] text-[var(--color-tone-warn)]">
          {t('skills.pending.recordsFailed')}
        </p>
      ) : (
        qualifications.map((qualification) => (
          <p key={qualification.id} className="text-[13px] text-[var(--color-surface-mute)]">
            {t('skills.pending.qualificationStarts', { date: qualification.startDay })}
          </p>
        ))
      )}
    </div>
  )
}

function PendingRow({ skill }: { skill: HeldSkill }) {
  return (
    <li className="flex flex-col gap-2 rounded-[18px] border border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-5 py-4">
      <p className="text-[17px] font-semibold text-[var(--color-surface-ink)]">
        {heldSkillTitle(skill)}
      </p>
      <PendingReason skill={skill} />
      <SkillBadges skill={skill} />
    </li>
  )
}

/**
 * One held skill: what it is, what it rests on, and the one verb that changes
 * who may read it.
 *
 * The endorsements behind it are a disclosure rather than part of the row. They
 * are a separate read per skill (the contract has no route that returns them
 * all), so opening the row is what asks for them.
 */
function HeldRow({
  skill,
  onPublish,
  onUnpublish,
  busy,
}: {
  skill: HeldSkill
  onPublish: (skill: HeldSkill) => void
  onUnpublish: (skill: HeldSkill) => void
  busy: boolean
}) {
  const { t } = useTranslation()
  const [showEndorsements, setShowEndorsements] = useState(false)
  const published = skill.visibility === 'published'
  const endorsed = skill.basis.includes('endorsement')
  const title = heldSkillTitle(skill)

  return (
    <li className="flex flex-col gap-3 rounded-[18px] border border-[var(--color-surface-border)] bg-[var(--color-surface-bg)] px-5 py-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-[17px] font-semibold text-[var(--color-surface-ink)]">{title}</p>
          <div className="mt-1.5">
            <SkillBadges skill={skill} />
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {published ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => onUnpublish(skill)}
              disabled={busy}
              className="min-h-11 rounded-full px-4 text-[var(--color-surface-mute)]"
              aria-label={t('skills.held.unpublishAriaLabel', { label: title })}
            >
              {busy ? <Spinner className="h-4 w-4" /> : null}
              {t('skills.held.unpublish')}
            </Button>
          ) : (
            <Button
              type="button"
              size="pill"
              onClick={() => onPublish(skill)}
              disabled={busy}
              className="min-h-11"
              aria-label={t('skills.held.publishAriaLabel', { label: title })}
            >
              {busy ? <Spinner className="h-4 w-4" /> : null}
              {t('skills.held.publish')}
            </Button>
          )}
        </div>
      </div>

      {endorsed ? (
        <div>
          <button
            type="button"
            onClick={() => setShowEndorsements((open) => !open)}
            aria-expanded={showEndorsements}
            className="flex min-h-11 items-center gap-1 text-[15px] font-medium text-[var(--color-primary-500)] active:scale-95"
          >
            {showEndorsements ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            {t('skills.held.showEndorsements')}
          </button>
          {showEndorsements ? (
            <div className="mt-2">
              <PeerEndorsements skillKey={skill.key} />
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  )
}

export function SkillsListView({
  skills,
  onAccept,
  onReject,
  onPublish,
  onUnpublish,
  busyKey,
}: SkillsListViewProps) {
  const { t } = useTranslation()
  const proposed = skills.filter((s) => s.state === 'proposed')
  const pending = skills.filter((s) => s.state === 'pending')
  const held = skills.filter((s) => s.state === 'held')
  const rejected = skills.filter((s) => s.state === 'rejected')

  return (
    <div className="space-y-8">
      <Section
        id="skills-proposed-heading"
        title={t('skills.proposed.title')}
        emptyText={t('skills.proposed.empty')}
        isEmpty={proposed.length === 0}
      >
        <ul className="space-y-3">
          {proposed.map((skill) => (
            <ProposalCard
              key={skill.key}
              skill={skill}
              onAccept={onAccept}
              onReject={onReject}
              busy={busyKey === skill.key}
            />
          ))}
        </ul>
      </Section>

      <Section
        id="skills-pending-heading"
        title={t('skills.pending.title')}
        emptyText={t('skills.pending.empty')}
        isEmpty={pending.length === 0}
      >
        <ul className="space-y-3">
          {pending.map((skill) => (
            <PendingRow key={skill.key} skill={skill} />
          ))}
        </ul>
      </Section>

      <Section
        id="skills-held-heading"
        title={t('skills.held.title')}
        emptyText={t('skills.held.empty')}
        isEmpty={held.length === 0}
      >
        <ul className="space-y-3">
          {held.map((skill) => (
            <HeldRow
              key={skill.key}
              skill={skill}
              onPublish={onPublish}
              onUnpublish={onUnpublish}
              busy={busyKey === skill.key}
            />
          ))}
        </ul>
      </Section>

      {/* Rendered only when there is something to show: an empty "rejected"
          heading on every profile would be a section about nothing. */}
      {rejected.length > 0 ? (
        <section aria-labelledby="skills-rejected-heading">
          <h2
            id="skills-rejected-heading"
            className="mb-3 text-[15px] font-semibold text-[var(--color-surface-mute)]"
          >
            {t('skills.rejected.title')}
          </h2>
          <ul className="space-y-3">
            {rejected.map((skill) => (
              <li
                key={skill.key}
                className="rounded-[18px] border border-dashed border-[var(--color-surface-border)] px-5 py-4"
              >
                <p className="text-[15px] text-[var(--color-surface-mute)]">{heldSkillTitle(skill)}</p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}

export default SkillsListView
